import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { useState } from 'react';
import { AppBar, Tab } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import CirculationSettings from './Ventilation/CirculationSettings.js';
import { useScheduledSettings, ScheduleDraftStatus } from './Scheduling/ScheduledSettings';
import { ShowSetting, ShowSettingMinMax } from '../FormWidgets/ShowSliderSetting';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { SettingGroupGrid } from '../FormWidgets/SettingGroup';

import { useCo2Settings } from '../Hooks/useCo2Settings';
import { supportsCo2SettingsRelocation } from '../Utils/co2_utils';
import { SettingActions } from '../components';
import { useSnackbar } from 'notistack';
import { getAdvertisedCirculationCount, supportedVentilationSettings, supportsCirculationSettings } from '../Utils/ventilation_utils';

export default function VentilationSettings({ ...other }) {
  const {
    t,
    controller,
    parent,
    controllerSnapshot,
    parentSnapshot,
    values: ordinaryValues,
    controls: ordinaryControls,
    openLoadSettingsDialog,
    closeLoadSettingsDialog
  } = useCo2Settings();
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const saveSettings = ordinaryControls.saveSettings;
  ordinaryControls.saveSettings = () =>
    saveSettings((settings) => supportedVentilationSettings(controller, parent, settings), true);
  const schedule = useScheduledSettings(controller, parent, 'ventilation', ordinaryValues, ordinaryControls);
  const { values, controls } = schedule;

  const [section, setSection] = useState('circulation');
  const circulationSupported =
    supportsCirculationSettings(controllerSnapshot, parentSnapshot) &&
    Array.isArray(values.circulation) &&
    values.circulation.length === 4;
  const visibleSection = circulationSupported ? section : 'exchange';
  const circulationCount = getAdvertisedCirculationCount(controllerSnapshot, parentSnapshot);
  let { enqueueSnackbar } = useSnackbar();

  controls.onChangeCommitted = function (id, v, saveTemp = true) {
    if (!['pressure_percent_day', 'pressure_percent_night', 'pressure_percent_co2'].includes(id)) return;
    const phase = id.split('_')[2];
    const maximum = 100 + v - 127;
    const adjusted = v < 127
      ? ['ec', 'ac'].map((kind) => `exhaust_fan_${kind}_max_${phase}`)
        .filter((key) => values[key] > maximum)
      : [];
    // Persist the pressure and affected fan values together, including restores.
    controls.setValue([id, ...adjusted], [v, ...adjusted.map(() => maximum)], saveTemp);
    if (adjusted.length) {
      enqueueSnackbar(adjusted.map((key) => t(key)).join(', ') + ' ' + t('adjusted'), { variant: 'info' });
    }
  };

  const read_access_advanced = controllerSnapshot.access_rights.advanced.read;
  const write_access = controllerSnapshot.access_rights.ventilation.write;

  return (
    <>
      <ScheduleDraftStatus schedule={schedule} />
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={ordinaryControls}
      />

      {circulationSupported && (
        <TabContext value={visibleSection}>
          <AppBar position="static" color="default">
            <TabList
              centered
              onChange={(_, value) => setSection(value)}
              aria-label={t('ventilation')}
            >
              <Tab value="circulation" label={t('ventilation_circulation')} />
              <Tab value="exchange" label={t('ventilation_exchange')} />
            </TabList>
          </AppBar>
        </TabContext>
      )}
      {visibleSection === 'circulation' && (
        <>
          {circulationCount > 4 && (
            <Alert severity="info">
              {t('circulation_capacity_unsupported', { count: circulationCount, supported: 4 })}
            </Alert>
          )}
          <CirculationSettings
            schedule={schedule}
            controller={controller}
            values={values.circulation}
            saved={parentSnapshot.settings.ventilation.circulation}
            controls={controls}
            disabled={!write_access}
          />
        </>
      )}
      {visibleSection === 'exchange' && (
        <>
          {schedule.canSave && (
            <SettingActions
              title={connectionDisabledReason}
              on_default={schedule?.saving || connectionStatus !== 'good'}
              onReset={controls.resetSettings}
              onSave={controls.saveSettings}
            />
          )}
          <SettingGroupGrid controller={controllerSnapshot} id={'fans_day'}>
            {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="exhaust_fan_ec_max_day"
                show="exhaust_fan_ec_min_max_day"
                controller={controllerSnapshot}
                cur_value_max={values.exhaust_fan_ec_max_day}
                controller_value_max={parentSnapshot.settings.ventilation.exhaust_fan_ec_max_day}
                cur_value_min={values.exhaust_fan_ec_min_day}
                controller_value_min={parentSnapshot.settings.ventilation.exhaust_fan_ec_min_day}
                min={9}
                max={
                  values.pressure_percent_day - 127 < 0
                    ? 100 + (values.pressure_percent_day - 127)
                    : 100
                }
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="intake_fan_ec_max_day"
                show="intake_fan_ec_min_max_day"
                controller={controllerSnapshot}
                cur_value_max={values.intake_fan_ec_max_day}
                controller_value_max={parentSnapshot.settings.ventilation.intake_fan_ec_max_day}
                cur_value_min={values.intake_fan_ec_min_day}
                controller_value_min={parentSnapshot.settings.ventilation.intake_fan_ec_min_day}
                min={9}
                max={100}
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="exhaust_fan_ac_max_day"
                show="exhaust_fan_ac_min_max_day"
                controller={controllerSnapshot}
                cur_value_max={values.exhaust_fan_ac_max_day}
                controller_value_max={parentSnapshot.settings.ventilation.exhaust_fan_ac_max_day}
                cur_value_min={values.exhaust_fan_ac_min_day}
                controller_value_min={parentSnapshot.settings.ventilation.exhaust_fan_ac_min_day}
                min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0}
                max={
                  values.pressure_percent_day - 127 < 0
                    ? 100 + (values.pressure_percent_day - 127)
                    : 100
                }
                off_at={
                  read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0
                }
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="intake_fan_ac_max_day"
                show="intake_fan_ac_min_max_day"
                controller={controllerSnapshot}
                cur_value_max={values.intake_fan_ac_max_day}
                controller_value_max={parentSnapshot.settings.ventilation.intake_fan_ac_max_day}
                cur_value_min={values.intake_fan_ac_min_day}
                controller_value_min={parentSnapshot.settings.ventilation.intake_fan_ac_min_day}
                min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0}
                max={100}
                off_at={
                  read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0
                }
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            <ShowSetting
              id="pressure_percent_day"
              controller={controllerSnapshot}
              cur_value={values.pressure_percent_day}
              controller_value={parentSnapshot.settings.ventilation.pressure_percent_day}
              controls={controls}
              disabled={!write_access}
              type="negative"
            />
          </SettingGroupGrid>

          <SettingGroupGrid controller={controllerSnapshot} id={'fans_night'}>
            {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="exhaust_fan_ec_max_night"
                show="exhaust_fan_ec_min_max_night"
                controller={controllerSnapshot}
                cur_value_max={values.exhaust_fan_ec_max_night}
                controller_value_max={parentSnapshot.settings.ventilation.exhaust_fan_ec_max_night}
                cur_value_min={values.exhaust_fan_ec_min_night}
                controller_value_min={parentSnapshot.settings.ventilation.exhaust_fan_ec_min_night}
                min={9}
                max={
                  values.pressure_percent_night - 127 < 0
                    ? 100 + (values.pressure_percent_night - 127)
                    : 100
                }
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="intake_fan_ec_max_night"
                show="intake_fan_ec_min_max_night"
                controller={controllerSnapshot}
                cur_value_max={values.intake_fan_ec_max_night}
                controller_value_max={parentSnapshot.settings.ventilation.intake_fan_ec_max_night}
                cur_value_min={values.intake_fan_ec_min_night}
                controller_value_min={parentSnapshot.settings.ventilation.intake_fan_ec_min_night}
                min={9}
                max={100}
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="exhaust_fan_ac_max_night"
                show="exhaust_fan_ac_min_max_night"
                controller={controllerSnapshot}
                cur_value_max={values.exhaust_fan_ac_max_night}
                controller_value_max={parentSnapshot.settings.ventilation.exhaust_fan_ac_max_night}
                cur_value_min={values.exhaust_fan_ac_min_night}
                controller_value_min={parentSnapshot.settings.ventilation.exhaust_fan_ac_min_night}
                min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0}
                max={
                  values.pressure_percent_night - 127 < 0
                    ? 100 + (values.pressure_percent_night - 127)
                    : 100
                }
                off_at={
                  read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0
                }
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="intake_fan_ac_max_night"
                show="intake_fan_ac_min_max_night"
                controller={controllerSnapshot}
                cur_value_max={values.intake_fan_ac_max_night}
                controller_value_max={parentSnapshot.settings.ventilation.intake_fan_ac_max_night}
                cur_value_min={values.intake_fan_ac_min_night}
                controller_value_min={parentSnapshot.settings.ventilation.intake_fan_ac_min_night}
                min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0}
                max={100}
                off_at={
                  read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0
                }
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            <ShowSetting
              id="pressure_percent_night"
              controller={controllerSnapshot}
              cur_value={values.pressure_percent_night}
              controller_value={parentSnapshot.settings.ventilation.pressure_percent_night}
              controls={controls}
              disabled={!write_access}
              type="negative"
            />
          </SettingGroupGrid>

          {supportsCo2SettingsRelocation(controllerSnapshot) && (
          <SettingGroupGrid controller={controllerSnapshot} id={'fans_co2'}>
            {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="exhaust_fan_ec_max_co2"
                show="exhaust_fans_ec_min_max_co2"
                controller={controllerSnapshot}
                cur_value_max={values.exhaust_fan_ec_max_co2}
                controller_value_max={parentSnapshot.settings.ventilation.exhaust_fan_ec_max_co2}
                cur_value_min={values.exhaust_fan_ec_min_co2}
                controller_value_min={parentSnapshot.settings.ventilation.exhaust_fan_ec_min_co2}
                min={9}
                max={
                  values.pressure_percent_co2 - 127 < 0
                    ? 100 + (values.pressure_percent_co2 - 127)
                    : 100
                }
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="intake_fan_ec_max_co2"
                show="intake_fan_ec_min_max_co2"
                controller={controllerSnapshot}
                cur_value_max={values.intake_fan_ec_max_co2}
                controller_value_max={parentSnapshot.settings.ventilation.intake_fan_ec_max_co2}
                cur_value_min={values.intake_fan_ec_min_co2}
                controller_value_min={parentSnapshot.settings.ventilation.intake_fan_ec_min_co2}
                min={9}
                max={100}
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="exhaust_fan_ac_max_co2"
                show="exhaust_fans_ac_min_max_co2"
                controller={controllerSnapshot}
                cur_value_max={values.exhaust_fan_ac_max_co2}
                controller_value_max={parentSnapshot.settings.ventilation.exhaust_fan_ac_max_co2}
                cur_value_min={values.exhaust_fan_ac_min_co2}
                controller_value_min={parentSnapshot.settings.ventilation.exhaust_fan_ac_min_co2}
                min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0}
                max={
                  values.pressure_percent_co2 - 127 < 0
                    ? 100 + (values.pressure_percent_co2 - 127)
                    : 100
                }
                off_at={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0}
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
              <ShowSettingMinMax
                id="intake_fan_ac_max_co2"
                show="intake_fan_ac_min_max_co2"
                controller={controllerSnapshot}
                cur_value_max={values.intake_fan_ac_max_co2}
                controller_value_max={parentSnapshot.settings.ventilation.intake_fan_ac_max_co2}
                cur_value_min={values.intake_fan_ac_min_co2}
                controller_value_min={parentSnapshot.settings.ventilation.intake_fan_ac_min_co2}
                min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0}
                max={100}
                off_at={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0}
                controls={controls}
                disabled={!write_access}
                type="fan_min_max"
              />
            )}
            <ShowSetting
              id="pressure_percent_co2"
              controller={controllerSnapshot}
              cur_value={values.pressure_percent_co2}
              controller_value={parentSnapshot.settings.ventilation.pressure_percent_co2}
              controls={controls}
              disabled={!write_access}
              type="negative"
            />
          </SettingGroupGrid>
          )}

          <SettingGroupGrid controller={controllerSnapshot} id={'deviation_rpm_reduction'}>
            <ShowSetting
              id="deviation_rpm_reduction"
              controller={controllerSnapshot}
              cur_value={values.deviation_rpm_reduction}
              controller_value={parentSnapshot.settings.ventilation.deviation_rpm_reduction}
              controls={controls}
              disabled={!write_access}
              type="float"
            />
          </SettingGroupGrid>

          <SettingGroupGrid controller={controllerSnapshot} id={'neg_pressure_header'}>
            <ShowSetting
              id="pressure_pa"
              controller={controllerSnapshot}
              cur_value={values.pressure_pa}
              controller_value={parentSnapshot.settings.ventilation.pressure_pa}
              controls={controls}
              disabled={!write_access}
            />
          </SettingGroupGrid>
        </>
      )}

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={schedule?.saving || connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={controls.saveSettings}
        />
      )}
    </>
  );
}
